import {
  ROCK_PUZZLE_COLS,
  ROCK_PUZZLE_ROWS,
  ROCK_PUZZLE_TILE_COUNT,
  type RockTileRotation,
} from './secondChanceRules';

export type RockPipeSide = 'top' | 'right' | 'bottom' | 'left';
export type RockPipeShape = 'straight' | 'corner' | 'tee' | 'empty';

export type RockPipeEndpoint = {
  col: number;
  row: number;
  side: RockPipeSide;
};

export type RockPipeCellDef = {
  shape: RockPipeShape;
  /** Ориентация детали в решённом пути (для тестов / справки). */
  solvedRotation: RockTileRotation;
};

export type RockPipeVariant = {
  id: number;
  a: RockPipeEndpoint;
  b: RockPipeEndpoint;
  /** 6 ячеек, index = row * COLS + col. */
  cells: readonly RockPipeCellDef[];
};

export type RockPipeBoardCell = {
  shape: RockPipeShape;
  rotation: RockTileRotation;
};

export type RockPipeBoard = {
  variant: RockPipeVariant;
  cells: RockPipeBoardCell[];
};

const SIDE_TO_INDEX: Record<RockPipeSide, number> = {
  top: 0,
  right: 1,
  bottom: 2,
  left: 3,
};

const INDEX_TO_SIDE: RockPipeSide[] = ['top', 'right', 'bottom', 'left'];

const OPPOSITE: Record<RockPipeSide, RockPipeSide> = {
  top: 'bottom',
  right: 'left',
  bottom: 'top',
  left: 'right',
};

const DELTA: Record<RockPipeSide, readonly [number, number]> = {
  top: [0, -1],
  right: [1, 0],
  bottom: [0, 1],
  left: [-1, 0],
};

/** Ports at rotation 0 (CW steps from asset default). */
const SHAPE_PORTS0: Record<Exclude<RockPipeShape, 'empty'>, readonly number[]> = {
  straight: [SIDE_TO_INDEX.left, SIDE_TO_INDEX.right],
  corner: [SIDE_TO_INDEX.top, SIDE_TO_INDEX.left],
  tee: [SIDE_TO_INDEX.top, SIDE_TO_INDEX.left, SIDE_TO_INDEX.right],
};

function cell(
  shape: RockPipeShape,
  solvedRotation: RockTileRotation = 0,
): RockPipeCellDef {
  return { shape, solvedRotation };
}

function ep(col: number, row: number, side: RockPipeSide): RockPipeEndpoint {
  return { col, row, side };
}

/**
 * 15 пресетов из `.dev/rock/variants.png` (сетка 2×3).
 * cells: row-major [TL, TR, ML, MR, BL, BR].
 */
export const ROCK_PIPE_VARIANTS: readonly RockPipeVariant[] = [
  {
    // 01: S через все 6
    id: 1,
    a: ep(0, 0, 'left'),
    b: ep(1, 2, 'right'),
    cells: [
      cell('straight', 0),
      cell('corner', 3),
      cell('corner', 2),
      cell('corner', 0),
      cell('corner', 1),
      cell('straight', 0),
    ],
  },
  {
    // 02: U сверху
    id: 2,
    a: ep(0, 0, 'top'),
    b: ep(1, 0, 'right'),
    cells: [
      cell('straight', 1),
      cell('corner', 2),
      cell('corner', 1),
      cell('corner', 0),
      cell('empty'),
      cell('empty'),
    ],
  },
  {
    // 03: петля, оба выхода слева
    id: 3,
    a: ep(0, 2, 'left'),
    b: ep(0, 1, 'left'),
    cells: [
      cell('corner', 2),
      cell('corner', 3),
      cell('tee', 2),
      cell('corner', 0),
      cell('corner', 0),
      cell('empty'),
    ],
  },
  {
    // 04: змейка
    id: 4,
    a: ep(1, 2, 'right'),
    b: ep(1, 1, 'right'),
    cells: [
      cell('corner', 2),
      cell('corner', 3),
      cell('straight', 1),
      cell('corner', 1),
      cell('corner', 1),
      cell('straight', 0),
    ],
  },
  {
    // 05
    id: 5,
    a: ep(1, 0, 'top'),
    b: ep(1, 2, 'bottom'),
    cells: [
      cell('corner', 2),
      cell('corner', 0),
      cell('straight', 1),
      cell('empty'),
      cell('corner', 1),
      cell('corner', 3),
    ],
  },
  {
    // 06
    id: 6,
    a: ep(0, 1, 'left'),
    b: ep(1, 2, 'bottom'),
    cells: [
      cell('corner', 2),
      cell('corner', 3),
      cell('corner', 0),
      cell('straight', 1),
      cell('empty'),
      cell('straight', 1),
    ],
  },
  {
    // 07
    id: 7,
    a: ep(0, 0, 'top'),
    b: ep(1, 1, 'right'),
    cells: [
      cell('straight', 1),
      cell('empty'),
      cell('straight', 1),
      cell('corner', 2),
      cell('corner', 1),
      cell('corner', 0),
    ],
  },
  {
    // 08
    id: 8,
    a: ep(0, 0, 'top'),
    b: ep(0, 2, 'left'),
    cells: [
      cell('corner', 1),
      cell('corner', 3),
      cell('empty'),
      cell('straight', 1),
      cell('straight', 0),
      cell('corner', 0),
    ],
  },
  {
    // 09
    id: 9,
    a: ep(1, 0, 'top'),
    b: ep(1, 2, 'right'),
    cells: [
      cell('corner', 2),
      cell('corner', 0),
      cell('straight', 1),
      cell('empty'),
      cell('corner', 1),
      cell('straight', 0),
    ],
  },
  {
    // 10
    id: 10,
    a: ep(0, 0, 'left'),
    b: ep(1, 2, 'right'),
    cells: [
      cell('straight', 0),
      cell('corner', 3),
      cell('empty'),
      cell('straight', 1),
      cell('empty'),
      cell('corner', 1),
    ],
  },
  {
    // 11
    id: 11,
    a: ep(0, 2, 'left'),
    b: ep(1, 1, 'right'),
    cells: [
      cell('corner', 2),
      cell('corner', 3),
      cell('straight', 1),
      cell('corner', 1),
      cell('corner', 0),
      cell('empty'),
    ],
  },
  {
    // 12
    id: 12,
    a: ep(0, 2, 'left'),
    b: ep(1, 0, 'right'),
    cells: [
      cell('empty'),
      cell('corner', 2),
      cell('empty'),
      cell('straight', 1),
      cell('straight', 0),
      cell('corner', 0),
    ],
  },
  {
    // 13
    id: 13,
    a: ep(0, 2, 'left'),
    b: ep(0, 0, 'top'),
    cells: [
      cell('corner', 1),
      cell('corner', 3),
      cell('empty'),
      cell('straight', 1),
      cell('straight', 0),
      cell('corner', 0),
    ],
  },
  {
    // 14: оба выхода справа на правом столбце
    id: 14,
    a: ep(1, 0, 'right'),
    b: ep(1, 2, 'right'),
    cells: [
      cell('empty'),
      cell('corner', 2),
      cell('empty'),
      cell('straight', 1),
      cell('empty'),
      cell('corner', 1),
    ],
  },
  {
    // 15
    id: 15,
    a: ep(0, 0, 'top'),
    b: ep(1, 2, 'right'),
    cells: [
      cell('straight', 1),
      cell('empty'),
      cell('corner', 1),
      cell('corner', 3),
      cell('empty'),
      cell('corner', 1),
    ],
  },
];

export function rockPipeCellIndex(col: number, row: number): number {
  return row * ROCK_PUZZLE_COLS + col;
}

export function rockPipeCoords(index: number): { col: number; row: number } {
  return {
    col: index % ROCK_PUZZLE_COLS,
    row: Math.floor(index / ROCK_PUZZLE_COLS),
  };
}

export function rotateRockPipeSide(side: RockPipeSide, rotation: RockTileRotation): RockPipeSide {
  const next = (SIDE_TO_INDEX[side] + rotation) % 4;
  return INDEX_TO_SIDE[next]!;
}

export function getRockPipePorts(
  shape: RockPipeShape,
  rotation: RockTileRotation,
): ReadonlySet<RockPipeSide> {
  if (shape === 'empty') {
    return new Set();
  }
  const ports = new Set<RockPipeSide>();
  for (const base of SHAPE_PORTS0[shape]) {
    ports.add(INDEX_TO_SIDE[(base + rotation) % 4]!);
  }
  return ports;
}

export function pickRockPipeVariant(random: () => number = Math.random): RockPipeVariant {
  const index = Math.floor(random() * ROCK_PIPE_VARIANTS.length);
  return ROCK_PIPE_VARIANTS[index]!;
}

export function createSolvedRockPipeBoard(variant: RockPipeVariant): RockPipeBoard {
  return {
    variant,
    cells: variant.cells.map((c) => ({
      shape: c.shape,
      rotation: c.solvedRotation,
    })),
  };
}

export function createRockPipeBoard(
  variant: RockPipeVariant = pickRockPipeVariant(),
  random: () => number = Math.random,
): RockPipeBoard {
  const board: RockPipeBoard = {
    variant,
    cells: variant.cells.map((c) => ({
      shape: c.shape,
      rotation:
        c.shape === 'empty'
          ? 0
          : ((Math.floor(random() * 4) as RockTileRotation)),
    })),
  };

  if (isRockPipePathConnected(board)) {
    const rotatable = board.cells.findIndex((c) => c.shape !== 'empty');
    if (rotatable >= 0) {
      const cell = board.cells[rotatable]!;
      board.cells[rotatable] = {
        ...cell,
        rotation: (((cell.rotation + 1) % 4) as RockTileRotation),
      };
    }
  }

  return board;
}

export function rotateRockPipeTileAt(
  board: RockPipeBoard,
  index: number,
): RockPipeBoard {
  const cell = board.cells[index];
  if (cell == null || cell.shape === 'empty') {
    return board;
  }
  const cells = board.cells.slice();
  cells[index] = {
    ...cell,
    rotation: (((cell.rotation + 1) % 4) as RockTileRotation),
  };
  return { ...board, cells };
}

function neighborIndex(col: number, row: number, side: RockPipeSide): number | null {
  const [dx, dy] = DELTA[side];
  const nc = col + dx;
  const nr = row + dy;
  if (nc < 0 || nc >= ROCK_PUZZLE_COLS || nr < 0 || nr >= ROCK_PUZZLE_ROWS) {
    return null;
  }
  return rockPipeCellIndex(nc, nr);
}

/** Путь от A к B: вход в ячейку A со стороны a.side, выход из B со стороны b.side. */
export function isRockPipePathConnected(board: RockPipeBoard): boolean {
  const { a, b } = board.variant;
  if (
    a.col < 0 ||
    a.col >= ROCK_PUZZLE_COLS ||
    a.row < 0 ||
    a.row >= ROCK_PUZZLE_ROWS ||
    b.col < 0 ||
    b.col >= ROCK_PUZZLE_COLS ||
    b.row < 0 ||
    b.row >= ROCK_PUZZLE_ROWS
  ) {
    return false;
  }

  const startIndex = rockPipeCellIndex(a.col, a.row);
  const endIndex = rockPipeCellIndex(b.col, b.row);
  const startPorts = getRockPipePorts(
    board.cells[startIndex]!.shape,
    board.cells[startIndex]!.rotation,
  );
  if (!startPorts.has(a.side)) {
    return false;
  }

  const visited = new Set<number>();
  const queue: number[] = [startIndex];
  visited.add(startIndex);

  while (queue.length > 0) {
    const index = queue.shift()!;
    const { col, row } = rockPipeCoords(index);
    const ports = getRockPipePorts(board.cells[index]!.shape, board.cells[index]!.rotation);

    if (index === endIndex && ports.has(b.side)) {
      return true;
    }

    for (const side of ports) {
      if (index === endIndex && side === b.side) {
        continue;
      }
      const next = neighborIndex(col, row, side);
      if (next == null || visited.has(next)) {
        continue;
      }
      const nextPorts = getRockPipePorts(
        board.cells[next]!.shape,
        board.cells[next]!.rotation,
      );
      if (!nextPorts.has(OPPOSITE[side])) {
        continue;
      }
      visited.add(next);
      queue.push(next);
    }
  }

  return false;
}

/**
 * finish.png: круг справа, квадратный хвостик слева при 0°.
 * Крутим так, чтобы хвостик смотрел наружу от клетки (не на трубы).
 */
export function finishRotationForSide(side: RockPipeSide): number {
  switch (side) {
    case 'right':
      return 180;
    case 'bottom':
      return 270;
    case 'left':
      return 0;
    case 'top':
      return 90;
    default: {
      const _exhaustive: never = side;
      return _exhaustive;
    }
  }
}
